// THE LINE ITEM'S ICON BUTTON (owner, 2026-09-25 — D-154): *"the icon should
// light-up blue when there is something there, and it should have a red
// border when something required is empty."*
//
// One component for both icons a line carries — Additional Detail and
// Associated Entities — in both tables, so the two states look the same
// everywhere:
//  - BLUE: the Filled variant of the icon in `colorBrandForeground1`. Empty
//    is the Regular variant in the default subtle grey. Shape and colour both
//    change, so the state does not rest on colour alone.
//  - RED BORDER: `useRequiredStyles().requiredIcon`, the same red token every
//    required box in the modal uses (`../lib/required.ts` owns when). The
//    border never recolours the glyph, so a line holding SOME of what it
//    needs reads as blue inside red, not as one or the other.
//
// A `forwardRef` Button, so it can sit directly inside a `PopoverTrigger`.
//
// `LineItemPopover` below is the one pop-up both icons open: the same Fluent
// `Popover`, the same surface, width and padding, and the same small
// uppercase heading, so the two pop-ups on a line look like one family.
import { forwardRef, useCallback, useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import {
  Button,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  mergeClasses,
  tokens,
  type ButtonProps,
} from '@fluentui/react-components'
import type { FluentIcon } from '@fluentui/react-icons'
import { useRequiredStyles } from '../lib/theme'
import { lineItemIconLabel } from '../lib/required'
import { withinDialogPositioning } from './popupPositioning'

const useStyles = makeStyles({
  filled: { color: tokens.colorBrandForeground1 },
  // Mounted inside the Dialog when there is one (see `LineItemPopover`), so it
  // shares a stacking context with the modal's sticky action bar (z-index 2).
  // Lift it clear.
  surface: { zIndex: 10 },
  // A comfortable width for the Associated Entities rows (2026-07-14); the
  // Additional Detail pop-up takes the same box so the two match.
  pop: { width: '540px', maxWidth: '82vw' },
})

interface LineItemIconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title' | 'aria-label'> {
  /** The Regular and Filled variants of one Fluent icon. */
  regularIcon: FluentIcon
  filledIcon: FluentIcon
  /** "Additional Detail" / "Associated Entities" — the accessible name. */
  label: string
  /** Something is there: blue, Filled. */
  filled: boolean
  /** A required value inside is empty and fillable here: red border. */
  requiredMissing: boolean
}

export const LineItemIconButton = forwardRef<HTMLButtonElement, LineItemIconButtonProps>(
  function LineItemIconButton(
    {
      regularIcon: Regular,
      filledIcon: Filled,
      label,
      filled,
      requiredMissing,
      className,
      ...rest
    },
    ref,
  ) {
    const s = useStyles()
    const rs = useRequiredStyles()
    const name = lineItemIconLabel(label, filled, requiredMissing)
    return (
      <Button
        // Whatever `PopoverTrigger` injects (`aria-expanded`, the click and
        // key handlers) passes straight through to the button.
        {...(rest as ButtonProps)}
        ref={ref}
        appearance="subtle"
        size="small"
        icon={filled ? <Filled /> : <Regular />}
        className={mergeClasses(filled && s.filled, requiredMissing && rs.requiredIcon, className)}
        aria-label={name}
        title={name}
      />
    )
  },
)

/**
 * The icon plus its pop-up.
 *
 * WHERE THE SURFACE MOUNTS. Inside the invoice Dialog when there is one — a
 * `PopoverSurface` portalled to `document.body` sits outside the Dialog's
 * focus trap, and tabster hauls focus back out of it a beat after every click
 * (verified in the browser, 2026-07-14; `EditableInvoiceForm.tsx` carries the
 * whole account). `mountNode` names the host outright; left `undefined`, the
 * nearest `[role="dialog"]` above the trigger is used, and with none (a queue
 * card) Fluent's own portal is fine.
 */
export function LineItemPopover({
  mountNode,
  children,
  ...button
}: LineItemIconButtonProps & { mountNode?: HTMLElement | null; children: ReactNode }) {
  const s = useStyles()
  const [dialogHost, setDialogHost] = useState<HTMLElement | null>(null)
  const findHost = useCallback((el: HTMLButtonElement | null) => {
    if (el) setDialogHost(el.closest('[role="dialog"]') as HTMLElement | null)
  }, [])
  const host = mountNode !== undefined ? mountNode : dialogHost
  return (
    // INSIDE THE MODAL (QA C1-09): right-aligned to the icon, and bounded by
    // the modal's own surface — `withinDialogPositioning`.
    <Popover mountNode={host ?? undefined} positioning={withinDialogPositioning(host)}>
      <PopoverTrigger disableButtonEnhancement>
        <LineItemIconButton {...button} ref={mountNode === undefined ? findHost : undefined} />
      </PopoverTrigger>
      <PopoverSurface className={s.surface}>
        <div className={s.pop}>{children}</div>
      </PopoverSurface>
    </Popover>
  )
}
